import { History, Location } from 'history';
import { useCallback, useMemo } from 'react';

import { SortFieldMap } from '../types/list';
import { parseSort, parseUrlParams, stringifyUrlParams } from '../util/listUrl';

interface UseTableUrlSyncArgs {
  location: Location;
  history: History;
  page: number;
  pageSize: number;
  sortFieldMap: SortFieldMap;
}

interface UseTableUrlSyncResult {
  currentSortField?: string;
  currentSortOrder?: 'ascend' | 'descend';
  onTableChange: (
    pagination: { current?: number; pageSize?: number },
    _filters: any,
    sorter: any
  ) => void;
}

export function useTableUrlSync({
  location,
  history,
  page,
  pageSize,
  sortFieldMap,
}: UseTableUrlSyncArgs): UseTableUrlSyncResult {
  const currentParams = useMemo(
    () => parseUrlParams(location.search),
    [location.search]
  );
  const { field: currentSortField, order: currentSortOrder } = useMemo(
    () => parseSort(currentParams.sort),
    [currentParams.sort]
  );

  const onTableChange = useCallback(
    (
      pagination: { current?: number; pageSize?: number },
      _filters: any,
      sorter: any
    ) => {
      const primarySorter = Array.isArray(sorter) ? sorter[0] : sorter;
      const fieldKey =
        (primarySorter?.field as string) ||
        primarySorter?.column?.dataIndex ||
        undefined;
      const apiField = fieldKey && sortFieldMap[fieldKey];
      const nextOrder =
        primarySorter?.order === 'ascend'
          ? 'asc'
          : primarySorter?.order === 'descend'
          ? 'desc'
          : undefined;

      const params = parseUrlParams(location.search);
      const nextParams: Record<string, string> = { ...params };

      const computedSort =
        apiField && nextOrder ? `${apiField}:${nextOrder}` : undefined;

      if (computedSort) {
        nextParams.sort = computedSort;
        nextParams.page = '1';
      } else if (fieldKey) {
        delete nextParams.sort;
        nextParams.page = '1';
      }

      const nextPageSize = pagination.pageSize || pageSize;
      const nextPage = pagination.current || page;
      if (String(nextPageSize) !== String(params.pageSize || pageSize)) {
        nextParams.pageSize = String(nextPageSize);
        nextParams.page = '1';
      } else if (String(nextPage) !== String(params.page || page)) {
        nextParams.page = String(nextPage);
      }

      const prevSearch = stringifyUrlParams(params);
      const newSearch = stringifyUrlParams(nextParams);
      if (prevSearch === newSearch) return;

      history.push({ pathname: location.pathname, search: newSearch });
    },
    [history, location.pathname, location.search, page, pageSize, sortFieldMap]
  );

  return { currentSortField, currentSortOrder, onTableChange };
}
